import { Signal, signal, WritableSignal } from '@angular/core';

import { TranslationKey } from '../consts/translation-key.type';

export type TranslationDictionary = Readonly<Record<string, string | undefined>>;
export type TranslationParams = Readonly<Record<string, string | number>>;

export const DEFAULT_LANGUAGE_CID = 'nl';
export const TRANSLATION_SCOPE = 'wpp';

/**
 * Copy that must render while the API is unreachable: the server-error modal is the one screen
 * shown exactly then. The only bundled texts in the app; the overlay wins whenever it has the cid.
 */
export const OFFLINE_COPY: Readonly<Partial<Record<TranslationKey, string>>> = {
  'wpp.common.error.title': 'Er ging iets mis',
  'wpp.component.server-error-modal.title-failed': 'Het is niet gelukt',
  'wpp.component.server-error-modal.body-counting':
    'Ons planningssysteem is op dit moment niet bereikbaar. Je hoeft niets te doen: we proberen het automatisch opnieuw.',
  'wpp.component.server-error-modal.countdown-label': 'seconden tot de volgende poging',
  'wpp.component.server-error-modal.body-retrying':
    'Ons planningssysteem is op dit moment niet bereikbaar.',
  'wpp.component.server-error-modal.retrying-label': 'We proberen het nu opnieuw…',
  'wpp.component.server-error-modal.body-failed':
    'Het is helaas niet gelukt om verbinding te maken met ons planningssysteem. Probeer het over een paar minuten opnieuw.',
  'wpp.component.server-error-modal.reload-label': 'Pagina opnieuw laden',
};

const overlay: WritableSignal<TranslationDictionary> = signal<TranslationDictionary>({});
const language: WritableSignal<string> = signal<string>(DEFAULT_LANGUAGE_CID);

/** Language the overlay was fetched for. Drives `x-language` once that stops being hardcoded. */
export const activeLanguage: Signal<string> = language.asReadonly();

/** Replaces `{name}` with params[name]. Unknown placeholders are left verbatim. */
export function interpolate(text: string, params: TranslationParams): string {
  return text.replace(/\{(\w+)\}/g, (placeholder: string, name: string): string =>
    name in params ? String(params[name]) : placeholder,
  );
}

/**
 * Copy for `cid` from the API-loaded overlay — the single source of translations, bar the
 * OFFLINE_COPY of the server-error modal. A missing key renders as its cid: visible, greppable,
 * and only possible when the backend scope is incomplete (the same backend everything else in
 * the app depends on anyway).
 */
export function t(cid: TranslationKey, params?: TranslationParams): string {
  const text: string = overlay()[cid] ?? OFFLINE_COPY[cid] ?? cid;

  return params === undefined ? text : interpolate(text, params);
}

/**
 * Narrows an untrusted payload. Anything that is not a plain object is rejected: an empty
 * scope answers `[]`, and an /api that is not edge-routed answers 200 with the SPA shell.
 */
export function toDictionary(payload: unknown): TranslationDictionary {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }

  const result: Record<string, string> = {};
  for (const [cid, value] of Object.entries(payload)) {
    // null means untranslated in this language and '' means nothing to render; both fall
    // through to the visible cid marker.
    if (typeof value === 'string' && value.length > 0) {
      result[cid] = value;
    }
  }

  return result;
}

export function setTranslations(
  payload: unknown,
  languageCid: string = DEFAULT_LANGUAGE_CID,
): void {
  overlay.set(toDictionary(payload));
  language.set(languageCid);
}
